import React from 'react';
import clsx from 'clsx';

import {Icon} from '@netx/core-icons';
import {Link, type LinkProps, Skeleton, Typography} from '@mui/material';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import {
	breadcrumbClasses,
	getBreadcrumbUtilityClass,
	type BreadcrumbClasses,
	BREADCRUMB_CLASS_NAME,
} from './breadcrumbClasses';
import {BreadcrumbObject} from './types';

export type BreadcrumbElement = HTMLLIElement;
export type BreadcrumbRef = React.ForwardedRef<BreadcrumbElement>;

type UtilityClassProps = WithClasses<BreadcrumbClasses, {}>;

const useUtilityClasses = (ownerState: UtilityClassProps) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
		icon: ['icon'],
		label: ['label'],
	};
	return composeClasses(slots, getBreadcrumbUtilityClass, classes);
};

const BreadcrumbRoot = styled('li', {
	name: BREADCRUMB_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	flex: '0 0 auto',
	display: 'flex',
	alignItems: 'center',
	overflow: 'hidden',
}));

const BreadcrumbIcon = styled(Icon, {
	name: BREADCRUMB_CLASS_NAME,
	slot: 'Icon',
})(({theme}) => ({
	flex: '0 0 auto',
	[`& + .${breadcrumbClasses.label}`]: {
		marginLeft: theme.spacing(0.5),
	},
}));

const BreadcrumbLink = styled(Link)(({theme}) => ({
	flex: '0 0 auto',
	display: 'flex',
	alignItems: 'center',
}));

export interface BreadcrumbProps
	extends
		BreadcrumbObject,
		React.DetailedHTMLProps<
			React.HTMLAttributes<BreadcrumbElement>,
			BreadcrumbElement
		>,
		WithClasses<BreadcrumbClasses, {}> {
	iconOnly?: boolean;
}

const BreadcrumbLinkOrNoLink = ({
	children,
	className,
	url,
	...props
}: React.PropsWithChildren<
	Pick<BreadcrumbProps, 'url'> & {
		className?: string;
		LinkProps?: Partial<LinkProps>;
	}
>) => {
	if (url)
		return (
			<BreadcrumbLink className={className} href={url} {...props}>
				{children}
			</BreadcrumbLink>
		);
	return <>{children}</>;
};

const BreadcrumbInner = ({
	classes: _classes,
	className,
	icon,
	iconOnly = false,
	label,
	loading,
	url,
	...props
}: BreadcrumbProps) => {
	const ownerState: UtilityClassProps = {
		classes: _classes,
		className,
	};

	const classes = useUtilityClasses(ownerState);

	return (
		<BreadcrumbRoot
			className={clsx(classes.root, className)}
			title={label}
			{...props}
		>
			{loading ? (
				<Skeleton
					role="alert"
					aria-busy="true"
					aria-live="polite"
					width={100}
					height={20}
				/>
			) : (
				<BreadcrumbLinkOrNoLink
					aria-label={iconOnly ? label : undefined}
					url={url}
				>
					{icon ? (
						<BreadcrumbIcon fixedWidth className={classes.icon} icon={icon} />
					) : null}

					{!iconOnly && label ? (
						<Typography className={classes.label} noWrap variant="body2">
							{label}
						</Typography>
					) : null}
				</BreadcrumbLinkOrNoLink>
			)}
		</BreadcrumbRoot>
	);
};

export const BreadcrumbPrimitive = BreadcrumbInner;

export const Breadcrumb = BreadcrumbPrimitive;

export default Breadcrumb;
